'use client';

import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

import {
  useCreateOrderMutation
} from "@/features/api/paymentApi";

export default function CheckoutPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state) => state.auth || {});

  /* ---------------- PRODUCT NORMALIZATION ---------------- */
  const product = useMemo(() => {
    const params = new URLSearchParams(location.search);

    const id = params.get("courseId");
    const slug = params.get("slug");

if (!id || !slug) return null;

    return {
      id,
      slug,
      name: params.get("courseName") || "Course",
      price: Number(params.get("price")) || 0,
      image: params.get("courseImage") || "",
      productType: params.get("type") || "course", 
    };
  }, [location.search]);

  /* ---------------- REDIRECT IF INVALID ---------------- */
  useEffect(() => {
    if (!product) {
      toast.error("No product selected.");
      navigate("/", { replace: true });
    }
  }, [product, navigate]);

  /* ---------------- AUTH CHECK ---------------- */
  useEffect(() => {
    if (isAuthenticated === false) {
      navigate("/login", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  /* ---------------- FORM STATE ---------------- */
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
  });

  const [termsAccepted, setTermsAccepted] = useState(false);

  /* ---------------- AUTO FILL ---------------- */
  useEffect(() => {
    if (isAuthenticated && user) {
      setFormData({
        name: user?.name || "",
        email: user?.email || "",
        phone: user?.phone || user?.mobile || "",
      });
    }
  }, [isAuthenticated, user]);

  /* ---------------- TAX CALCULATION ---------------- */
  const price = product?.price || 0;
  const cgst = Number((price * 0.09).toFixed(2));
  const sgst = Number((price * 0.09).toFixed(2));
  const totalAmount = Number((price + cgst + sgst).toFixed(2));

  /* ---------------- API ---------------- */
  const [createOrder, { isLoading }] = useCreateOrderMutation();

  /* ---------------- INPUT HANDLER ---------------- */
  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  /* ---------------- PAYMENT HANDLER ---------------- */
  const handlePayment = async (e) => {
    e.preventDefault();

    if (!termsAccepted) {
      toast.error("Please accept Terms & Conditions.");
      return;
    }

    const name = formData.name.trim();
    const email = formData.email.trim();
    const phone = formData.phone.replace(/\D/g, "");

    if (!name || !email || !phone) {
      toast.error("All fields are required.");
      return;
    }

    if (!/^\d{10}$/.test(phone)) {
      toast.error("Enter valid 10-digit phone number.");
      return;
    }

   try {
  /* -------- FREE COURSE -------- */
 if (totalAmount === 0) {
  const res = await createOrder({
    userId: user._id,
    name,
    email,
    phone,
    amount: 0,
    courseId: product.id,
    courseName: product.name,
    productType: "course",
  }).unwrap();

  toast.success("Enrolled successfully!");
navigate(`/courses/${product.slug}/learn`, { replace: true });  return;
}
      /* -------- PAID COURSE -------- */
     const payload = {
  userId: user._id,
  name,
  email,
  phone,
  amount: totalAmount,
  courseId: product.id,
  courseName: product.name,
  productType: product.productType,
};

      const res = await createOrder(payload).unwrap();

      if (res?.redirectUrl) {
        toast.success("Redirecting to payment...");
    window.location.href = res.redirectUrl;
   } else {
        toast.error("Unable to initiate payment.");
      }

    } catch (err) {
      console.error("Payment Error:", err);
      toast.error(err?.data?.message || "Payment failed");
    }
  };

  if (!product) return null;

  /* ---------------- UI ---------------- */
  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <form onSubmit={handlePayment} className="grid lg:grid-cols-3 gap-8">

        {/* Billing */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Billing Details</CardTitle>
              <CardDescription>
                Enter your information to complete the purchase
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input id="name" value={formData.name} onChange={handleInputChange} />
                </div>

                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="phone">Phone *</Label>
                <Input id="phone" value={formData.phone} onChange={handleInputChange} />
              </div>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="mt-1 h-5 w-5 cursor-pointer accent-blue-600"
                />
                <Label className="text-sm leading-relaxed cursor-pointer">
                  I agree to{" "}
                  <a
                    href="/terms"
                    className="text-blue-600 underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms & Conditions
                  </a>
                </Label>
              </div>

            </CardContent>
          </Card>
        </div>

        {/* Summary */}
        <div>
          <Card className="sticky top-6">
            <CardHeader>
              <CardTitle>Order Summary</CardTitle>
            </CardHeader>

            <CardContent className="space-y-4">
              {product.image && (
                <img
                  src={product.image}
                  alt={product.name}
                  className="rounded-lg"
                  onError={(e) => (e.currentTarget.style.display = "none")}
                />
              )}

              <h2 className="font-semibold text-lg">{product.name}</h2>

              <div className="text-sm space-y-2">
                <div className="flex justify-between">
                  <span>Price</span>
                  <span>₹{price.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>CGST</span>
                  <span>₹{cgst.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>SGST</span>
                  <span>₹{sgst.toFixed(2)}</span>
                </div>
                <hr />
                <div className="flex justify-between font-bold">
                  <span>Total</span>
                  <span>₹{totalAmount.toFixed(2)}</span>
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading || !termsAccepted}
                className="w-full"
              >
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {totalAmount === 0 ? "Enroll Free" : "Proceed to Pay"}
              </Button>

            </CardContent>
          </Card>
        </div>

      </form>
    </div>
  );
}